import React, { useState } from 'react';
import { InlineSpinner } from './loading';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Comment as RemarkIcon
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';

interface ApprovalModalProps {
  open: boolean;
  action: 'approve' | 'reject' | 'remark';
  onConfirm: (remarks: string) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  open,
  action,
  onConfirm,
  onCancel,
  loading = false,
  error
}) => {
  const [remarks, setRemarks] = useState('');

  const handleConfirm = () => {
    onConfirm(remarks);
    setRemarks(''); // Reset remarks after confirmation
  };

  const handleCancel = () => {
    setRemarks(''); // Reset remarks on cancel
    onCancel();
  };

  const getActionConfig = () => {
    switch (action) {
      case 'approve':
        return {
          title: 'Approve Proposal',
          icon: <ApproveIcon sx={{ color: SEARCE_COLORS.success, mr: 1 }} />,
          confirmText: 'Approve',
          confirmColor: 'success' as const,
          description: 'Are you sure you want to approve this proposal? You can add optional remarks below.',
          placeholder: 'Add optional remarks for approval...'
        };
      case 'reject':
        return {
          title: 'Reject Proposal',
          icon: <RejectIcon sx={{ color: SEARCE_COLORS.error, mr: 1 }} />,
          confirmText: 'Reject',
          confirmColor: 'error' as const,
          description: 'Are you sure you want to reject this proposal? Please provide a reason for rejection.',
          placeholder: 'Please provide a reason for rejection...'
        };
      case 'remark':
        return {
          title: 'Add Remark',
          icon: <RemarkIcon sx={{ color: SEARCE_COLORS.primary, mr: 1 }} />,
          confirmText: 'Add Remark',
          confirmColor: 'primary' as const,
          description: 'Add a remark to this proposal without approving or rejecting.',
          placeholder: 'Add your remark...'
        };
      default:
        return {
          title: 'Action',
          icon: null,
          confirmText: 'Confirm',
          confirmColor: 'primary' as const,
          description: '',
          placeholder: ''
        };
    }
  };

  const config = getActionConfig();
  const isRemarkRequired = action === 'reject';

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          {config.icon}
          <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
            {config.title}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body1" sx={{ mb: 3, color: SEARCE_COLORS.textSecondary }}>
          {config.description}
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={4}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={config.placeholder}
          variant="outlined"
          required={isRemarkRequired}
          error={isRemarkRequired && !remarks.trim()}
          helperText={isRemarkRequired && !remarks.trim() ? 'Reason for rejection is required' : ''}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: SEARCE_COLORS.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: SEARCE_COLORS.primary,
              },
            },
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleCancel}
          disabled={loading}
          sx={{
            color: SEARCE_COLORS.textSecondary,
            '&:hover': {
              backgroundColor: SEARCE_COLORS.backgroundHover,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={config.confirmColor}
          disabled={loading || (isRemarkRequired && !remarks.trim())}
          startIcon={loading ? <InlineSpinner size={16} thickness={5} /> : null}
          sx={{
            backgroundColor: config.confirmColor === 'success' ? SEARCE_COLORS.success :
                           config.confirmColor === 'error' ? SEARCE_COLORS.error :
                           SEARCE_COLORS.primary,
            '&:hover': {
              backgroundColor: config.confirmColor === 'success' ? SEARCE_COLORS.successDark :
                             config.confirmColor === 'error' ? SEARCE_COLORS.errorDark :
                             SEARCE_COLORS.primaryDark,
            },
            minWidth: 120,
          }}
        >
          {loading ? 'Processing...' : config.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalModal;
